import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Services from './components/Services';
import Cases from './components/Cases';
import Technologies from './components/Technologies';
import Process from './components/Process';
import WhyChooseUs from './components/WhyChooseUs';
import Contact from './components/Contact';
import Footer from './components/Footer';
import SignIn from './pages/SignIn';
import AdminDashboard from './pages/AdminDashboard';
import AdminProjects from './pages/AdminProjects';
import AdminClients from './pages/AdminClients';
import AdminBlogs from './pages/AdminBlogs';
import Blogs from './pages/Blogs';
import BlogDetail from './pages/BlogDetail';
import AboutUs from './pages/AboutUs';
import AllCases from './pages/AllCases';
import CaseDetail from './pages/CaseDetail';
import AllServices from './pages/AllServices';
import ServiceDetail from './pages/ServiceDetail';
import MarklyPrivacyPolicy from './pages/MarklyPrivacyPolicy';
import ContactsCleanerPrivacyPolicy from './pages/ContactsCleanerPrivacyPolicy';
import CarSpotPrivacyPolicy from './pages/CarSpotPrivacyPolicy';
import CarSpotTermsOfService from './pages/CarSpotTermsOfService';
import CarSpotSubscriptionTerms from './pages/CarSpotSubscriptionTerms';
import HaraldMedarbejderPrivacyPolicy from './pages/HaraldMedarbejderPrivacyPolicy';
import PriceCalculator from './pages/PriceCalculator';
import ProtectedRoute from './components/ProtectedRoute';
import PortalLayout from './components/PortalLayout';
import AdminLayout from './components/AdminLayout';
import ScrollToTop from './components/ScrollToTop';
import LanguageWrapper from './components/LanguageWrapper';
import CanonicalLink from './components/CanonicalLink';
import Overview from './pages/portal/Overview';
import Timeline from './pages/portal/Timeline';
import Documents from './pages/portal/Documents';
import Feedback from './pages/portal/Feedback';
import Issues from './pages/portal/Issues';
import Billing from './pages/portal/Billing';
import Support from './pages/portal/Support';
import { AuthProvider } from './contexts/AuthContext';
import Scheduler from './components/Scheduler';
import { useEffect } from 'react';

// Helper component for redirecting legacy routes with parameters
const LegacyRedirect = ({ basePath, paramName }) => {
  const params = require('react-router-dom').useParams();
  const paramValue = params[paramName];
  return <Navigate to={`/en/${basePath}/${paramValue}`} replace />;
};

function App() {
  useEffect(() => {
    // Allow browser scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'auto';
    }
  }, []);

  const AppContent = () => {
    const location = useLocation();

    return (
      <Routes>
        {/* Root redirect to default language */}
        <Route path="/" element={<Navigate to="/en" replace />} />

        {/* Language-prefixed public routes */}
        <Route path="/:lang" element={<LanguageWrapper />}>
          {/* Homepage */}
          <Route index element={
            <>
              <Navbar />
              <div className="App">
                <Hero />
                <Services />
                <Cases />
                <Technologies />
                <WhyChooseUs />
                <Process />
                <Contact />
                <Footer />
              </div>
            </>
          } />

          {/* About Us Route */}
          <Route path="about-us" element={
            <>
              <Navbar />
              <AboutUs key={location.pathname} />
              <Footer />
            </>
          } />

          {/* All Cases Route */}
          <Route path="cases" element={
            <>
              <Navbar />
              <AllCases />
              <Footer />
            </>
          } />

          {/* Markly Privacy Policy Route */}
          <Route path="cases/markly/privacypolicy" element={<MarklyPrivacyPolicy />} />

          {/* Contacts Cleaner Privacy Policy Route */}
          <Route path="cases/contacts-cleaner/privacypolicy" element={<ContactsCleanerPrivacyPolicy />} />

          {/* CarSpot Legal Routes */}
          <Route path="cases/carspot/privacypolicy" element={<CarSpotPrivacyPolicy />} />
          <Route path="cases/carspot/termsofservice" element={<CarSpotTermsOfService />} />
          <Route path="cases/carspot/subscriptionterms" element={<CarSpotSubscriptionTerms />} />

          {/* Harald Nyborg Medarbejder App Privacy Policy Route */}
          <Route path="cases/harald-medarbejder-app/privacypolicy" element={<HaraldMedarbejderPrivacyPolicy />} />

          {/* Individual Case Detail Route */}
          <Route path="cases/:caseId" element={
            <>
              <Navbar />
              <CaseDetail />
              <Footer />
            </>
          } />

          {/* All Services Route */}
          <Route path="services" element={<AllServices />} />

          {/* Individual Service Detail Route */}
          <Route path="services/:serviceId" element={<ServiceDetail />} />

          {/* Blog Routes */}
          <Route path="blogs" element={
            <>
              <Navbar />
              <Blogs />
              <Footer />
            </>
          } />

          {/* Individual Blog Detail Route */}
          <Route path="blogs/:slug" element={
            <>
              <Navbar />
              <BlogDetail />
              <Footer />
            </>
          } />

          {/* Sign In Route */}
          <Route path="sign-in" element={<SignIn />} />

          {/* Get Quote / Price Calculator Route */}
          <Route path="get-quote" element={<PriceCalculator />} />

          {/* Scheduler / Book Route */}
          <Route path="book" element={
            <>
              <Navbar />
              <div className="pt-20">
                <Scheduler />
              </div>
              <Footer />
            </>
          } />
        </Route>

        {/* Protected Admin Routes (not language-prefixed for simplicity) */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin={true}>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="projects" element={<AdminProjects />} />
          <Route path="projects/new" element={<AdminProjects openCreateModal={true} />} />
          <Route path="clients" element={<AdminClients />} />
          <Route path="clients/new" element={<AdminClients openCreateModal={true} />} />
          <Route path="blogs" element={<AdminBlogs />} />
          {/* Admin project detail pages - same as client portal but with edit capabilities */}
          <Route path="project/:projectId" element={<Overview />} />
          <Route path="project/:projectId/timeline" element={<Timeline />} />
          <Route path="project/:projectId/documents" element={<Documents />} />
          <Route path="project/:projectId/feedback" element={<Feedback />} />
          <Route path="project/:projectId/issues" element={<Issues />} />
          <Route path="project/:projectId/billing" element={<Billing />} />
          <Route path="project/:projectId/support" element={<Support />} />
        </Route>

        {/* Protected Client Portal Routes (not language-prefixed for simplicity) */}
        <Route
          path="/portal"
          element={
            <ProtectedRoute requireClient={true}>
              <PortalLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Overview />} />
          <Route path="timeline" element={<Timeline />} />
          <Route path="documents" element={<Documents />} />
          <Route path="feedback" element={<Feedback />} />
          <Route path="issues" element={<Issues />} />
          <Route path="billing" element={<Billing />} />
          <Route path="support" element={<Support />} />
        </Route>

        {/* Catch-all: redirect old routes to English versions */}
        <Route path="/about-us" element={<Navigate to="/en/about-us" replace />} />
        <Route path="/cases" element={<Navigate to="/en/cases" replace />} />
        <Route path="/cases/:caseId" element={<LegacyRedirect basePath="cases" paramName="caseId" />} />
        <Route path="/services" element={<Navigate to="/en/services" replace />} />
        <Route path="/services/:serviceId" element={<LegacyRedirect basePath="services" paramName="serviceId" />} />
        <Route path="/blogs" element={<Navigate to="/en/blogs" replace />} />
        <Route path="/blogs/:slug" element={<LegacyRedirect basePath="blogs" paramName="slug" />} />
        <Route path="/sign-in" element={<Navigate to="/en/sign-in" replace />} />
        <Route path="/book" element={<Navigate to="/en/book" replace />} />
      </Routes>
    );
  };

  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <CanonicalLink />
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
